//
// # DesignNameDialog
//
// Modal dialog for entering the full arbitrary-depth path for a new design.
// The user types the complete design path as a single slash-separated string
// (e.g. "library/cell/name"); it is stored verbatim. The selected type only
// sets the design's contentKind — it is not encoded as a path segment.
//
// This branch supports the "create" flow only. Duplicate and rename — which
// reuse this dialog's path input but seed it from an existing design and route
// to different workspace methods — are re-added in a later change together with
// their workspace methods and right-click-menu wiring.
//

import React, { useCallback, useEffect, useState } from "react";
import { useWorkspace } from "../../context";
import { extractErrorMessage } from "../../errorUtil";
import { CommandPaletteDialog } from "./CommandPaletteDialog";
import {
  buildDesignPath,
  formatPath,
  isValidPathInput,
  isValidSegment,
  pathClashes,
} from "./pathInput";
import {
  PathField,
  PATH_FORMAT_ERROR,
  SEGMENT_FORMAT_ERROR,
  type PathFieldMessage,
} from "./PathField";

export type DesignType = "layout" | "schematic" | "symbol";

// A discriminated union so duplicate/rename variants can be added later; only
// "create" (command palette and Explorer "+") exists for now.
export type DesignNameDialogMode = {
  readonly kind: "create";
  // Optional slash-separated parent path to pre-fill the Path field with (e.g.
  // when opened from a folder's "New Design..." context menu). Defaults empty.
  readonly initialPath?: string;
};

export interface DesignNameDialogProps {
  readonly mode: DesignNameDialogMode | null;
  readonly onClose: () => void;
}

export function DesignNameDialog({ mode, onClose }: DesignNameDialogProps) {
  const workspace = useWorkspace();

  const open = mode !== null;

  // Form state. `pathText` is the slash-separated *parent* path (e.g.
  // "library/cell"); `nameText` is the leaf name entered separately. The full
  // design path is the parent segments plus the leaf. The leaf defaults to the
  // selected type word (schematic/layout/symbol) to echo the old fixed
  // library/cell/view naming, and is overwritten whenever the type changes.
  const [designType, setDesignType] = useState<DesignType>("schematic");
  const [pathText, setPathText] = useState("");
  const [nameText, setNameText] = useState("schematic");
  const [pdkId, setPdkId] = useState<string>("");
  const [stackId, setStackId] = useState<string>("");

  // Server data
  const [pdks, setPdks] = useState<string[]>([]);
  const [stacks, setStacks] = useState<string[]>([]);

  // Existing full design-path strings (path.join("/")), used to block clashes
  // with content already in the depot.
  const [existingPaths, setExistingPaths] = useState<ReadonlySet<string>>(
    new Set()
  );

  // UI state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation state (shown after first submit attempt or on blur)
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Reset the form whenever the dialog opens.
  useEffect(() => {
    if (!mode) return;
    setError(null);
    setTouched({});
    setSubmitting(false);
    setDesignType("schematic");
    setPathText(mode.initialPath ?? "");
    setNameText("schematic");
    setPdkId("");
    setStackId("");
    setStacks([]);
  }, [mode]);

  // Fetch PDK list when dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);

    workspace
      .listPdks()
      .then((result) => {
        if (cancelled) return;
        if (result.err) {
          setError(`Failed to load PDKs: ${extractErrorMessage(result.val)}`);
          setPdks([]);
        } else {
          setPdks(result.val);
        }
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(extractErrorMessage(e));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, workspace]);

  // Load the set of existing full design paths once so we can block clashes
  // with content already in the depot as the user types.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void workspace.listContent().then((result) => {
      if (cancelled || result.err) return;
      setExistingPaths(new Set(result.val.map((id) => id.path.join("/"))));
    });
    return () => {
      cancelled = true;
    };
  }, [open, workspace]);

  // Fetch stacks when PDK changes (layout only)
  useEffect(() => {
    if (!open || designType !== "layout" || !pdkId) {
      setStacks([]);
      setStackId("");
      return;
    }

    let cancelled = false;
    setStacks([]);
    setStackId("");

    workspace
      .listStacks(pdkId)
      .then((result) => {
        if (cancelled) return;
        if (result.err) {
          setError(`Failed to load stacks: ${extractErrorMessage(result.val)}`);
        } else {
          setStacks(result.val);
          // Auto-select if only one stack available
          if (result.val.length === 1) {
            setStackId(result.val[0]);
          }
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(extractErrorMessage(e));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, designType, pdkId, workspace]);

  // Reset PDK/stack when design type changes
  useEffect(() => {
    setPdkId("");
    setStackId("");
    setStacks([]);
  }, [designType]);

  const markTouched = useCallback((field: string) => {
    setTouched((t) => ({ ...t, [field]: true }));
  }, []);

  // Validation. A parent path is required (no bare top-level leaves), as is a
  // valid leaf name.
  const pathValid = isValidPathInput(pathText);
  const nameValid = isValidSegment(nameText);
  const pdkRequired = designType === "layout";
  const pdkValid = !pdkRequired || pdkId.length > 0;
  const stackRequired = designType === "layout";
  const stackValid = !stackRequired || stackId.length > 0;

  // Full design path = parent segments + leaf name.
  const fullSegments = buildDesignPath(pathText, nameText);

  // Block clashes with existing depot content. One design per path, so any
  // existing design at this path clashes regardless of type.
  const clashes =
    pathValid && nameValid && pathClashes(existingPaths, fullSegments);

  const canSubmit =
    pathValid && nameValid && !clashes && pdkValid && stackValid && !submitting;

  // Parent-path field border + helper/error message.
  const pathMessage: PathFieldMessage =
    touched.path && !pathValid
      ? { text: PATH_FORMAT_ERROR, isError: true }
      : {
          text: "Parent path, e.g. library/cell.",
          isError: false,
        };

  // Leaf-name field border + helper/error message. The clash (which depends on
  // the full path) is surfaced here since the name completes the path.
  const nameFieldOk = nameValid && !clashes;
  const nameMessage: PathFieldMessage =
    touched.name && !nameValid
      ? { text: SEGMENT_FORMAT_ERROR, isError: true }
      : clashes
      ? {
          text: `A design already exists at ${formatPath(fullSegments)}.`,
          isError: true,
        }
      : {
          text: "Leaf name; defaults to the selected type.",
          isError: false,
        };

  const submitLabel = submitting ? "Creating..." : "Create";

  const handleSubmit = useCallback(async () => {
    if (!mode) return;
    // Mark all fields as touched to show validation errors
    setTouched({ path: true, name: true, pdkId: true, stackId: true });

    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    // Full design path = parent segments + leaf name. The selected type only
    // determines the contentKind of the created design, not the path.
    const fullPath = buildDesignPath(pathText, nameText);

    let identResult;
    if (designType === "layout") {
      identResult = await workspace.createNewLayout({
        path: fullPath,
        pdkId,
        stackId,
      });
    } else if (designType === "schematic") {
      identResult = await workspace.createNewSchematic({
        path: fullPath,
        pdkId: pdkId || undefined,
      });
    } else {
      identResult = await workspace.createNewSymbol({
        path: fullPath,
        pdkId: pdkId || undefined,
      });
    }

    if (identResult.err) {
      setError(
        `Failed to create ${designType}: ${extractErrorMessage(
          identResult.val
        )}`
      );
      setSubmitting(false);
      return;
    }

    if (identResult.val.kind !== "hierpath") {
      setError(`Unexpected ident kind: ${identResult.val.kind}`);
      setSubmitting(false);
      return;
    }
    onClose();
    workspace.handleDesignTreeChanged("design-added");
    void workspace.handleOpenRequest(identResult.val);
  }, [
    mode,
    canSubmit,
    designType,
    pathText,
    nameText,
    pdkId,
    stackId,
    workspace,
    onClose,
  ]);

  if (!open) return null;

  return (
    <CommandPaletteDialog
      title="New Design"
      hint="Enter to create · Escape to cancel"
      submitLabel={submitLabel}
      canSubmit={canSubmit}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
    >
      {/* Design type toggle */}
      <div className="add-instance-param-field">
        <label className="add-instance-param-label">Type</label>
        <div className="flex gap-1">
          {(["schematic", "layout", "symbol"] as const).map((t) => (
            <button
              key={t}
              onClick={() => {
                setDesignType(t);
                // Overwrite the leaf with the type word so the default path
                // echoes the old library/cell/view naming. The user can edit
                // it afterwards.
                setNameText(t);
              }}
              className="add-instance-btn flex-1 capitalize"
              style={{
                backgroundColor:
                  designType === t ? "var(--color-ide-accent)" : "transparent",
                borderColor:
                  designType === t
                    ? "var(--color-ide-accent)"
                    : "var(--color-ide-border)",
                color:
                  designType === t ? "#fff" : "var(--color-ide-text-muted)",
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Parent path */}
      <PathField
        label="Path"
        placeholder="library/cell"
        value={pathText}
        onChange={setPathText}
        onBlur={() => {
          markTouched("path");
        }}
        invalid={touched.path && !pathValid}
        message={pathMessage}
        autoFocus
      />

      {/* Leaf name */}
      <PathField
        label="Name"
        placeholder="schematic"
        value={nameText}
        onChange={setNameText}
        onBlur={() => {
          markTouched("name");
        }}
        invalid={touched.name && !nameFieldOk}
        message={nameMessage}
      />

      {/* PDK selector */}
      <div className="add-instance-param-field">
        <label className="add-instance-param-label">
          PDK{designType !== "layout" ? " (optional)" : ""}
        </label>
        {loading ? (
          <span className="add-instance-param-hint">Loading PDKs...</span>
        ) : (
          <select
            value={pdkId}
            onChange={(e) => {
              setPdkId(e.target.value);
            }}
            onBlur={() => {
              markTouched("pdkId");
            }}
            className={
              touched.pdkId && !pdkValid
                ? "add-instance-param-input add-instance-param-input-error"
                : "add-instance-param-input"
            }
            style={{ colorScheme: "dark" }}
          >
            {designType !== "layout" ? (
              <option value="">None (PDK-agnostic)</option>
            ) : (
              <option value="" disabled>
                Select a PDK...
              </option>
            )}
            {pdks.map((pdk) => (
              <option key={pdk} value={pdk}>
                {pdk}
              </option>
            ))}
          </select>
        )}
        {touched.pdkId && !pdkValid && (
          <span className="add-instance-param-error">
            A PDK is required for layouts
          </span>
        )}
      </div>

      {/* Stack selector (layout only) */}
      {designType === "layout" && pdkId && (
        <div className="add-instance-param-field">
          <label className="add-instance-param-label">Metal Stack</label>
          {stacks.length === 0 ? (
            <span className="add-instance-param-hint">Loading stacks...</span>
          ) : (
            <select
              value={stackId}
              onChange={(e) => {
                setStackId(e.target.value);
              }}
              onBlur={() => {
                markTouched("stackId");
              }}
              className={
                touched.stackId && !stackValid
                  ? "add-instance-param-input add-instance-param-input-error"
                  : "add-instance-param-input"
              }
              style={{ colorScheme: "dark" }}
            >
              <option value="" disabled>
                Select a stack...
              </option>
              {stacks.map((stack) => (
                <option key={stack} value={stack}>
                  {stack}
                </option>
              ))}
            </select>
          )}
          {touched.stackId && !stackValid && (
            <span className="add-instance-param-error">
              A metal stack is required for layouts
            </span>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && <span className="add-instance-param-error">{error}</span>}
    </CommandPaletteDialog>
  );
}
